import React from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Divider from 'Components/DesignSystem/Divider/Divider';
import { toast } from 'react-toastify';
import type { DropdownOption } from '../../../../types/DesignSystem/Dropdown';
import { EnvironmentCommendFormData } from '../Types/env.types';
import { ConfirmationDialog } from 'Pages/GMS/ISR/UI-Model/components/Common/ConfirmationDialog';

interface EnvironmentCommendTabProps {
  editData: EnvironmentCommendFormData;
  onFormChange: (data: EnvironmentCommendFormData) => void;
  isCompatioAppsEnabled: boolean;
}

const DEFAULT_MAX_CATEGORIES = 5;
const DEFAULT_MAX_PRODUCTS = 50;

const EnvironmentCommendTab: React.FC<EnvironmentCommendTabProps> = ({
  editData,
  onFormChange,
  isCompatioAppsEnabled
}) => {
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [showDisableARSDialog, setShowDisableARSDialog] = React.useState(false);

  const setValue = (key: keyof EnvironmentCommendFormData, value: any) => {
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }

    onFormChange({
      ...editData,
      [key]: value,
    });
  };

  const validateMaxCategories = (value: number): string | null => {
    if (isNaN(value)) {
      return 'Maximum Categories is required';
    }
    if (value < 1 || value > 10) {
      return 'Must be a number between 1 and 10';
    }
    return null;
  };

  const validateMaxProducts = (value: number): string | null => {
    if (isNaN(value)) {
      return 'Maximum Products is required';
    }
    if (value < 1 || value > 100) {
      return 'Must be a number between 1 and 100';
    }
    return null;
  };

  const handleEnableARSChange = (checked: boolean) => {
    if (!checked) {
      setShowDisableARSDialog(true);
    } else {
      setValue('enable_ars', true);
    }
  };

  const handleDisableARSConfirm = () => {
    setValue('enable_ars', false);
    setShowDisableARSDialog(false);
  };

  const handleDisableARSCancel = () => {
    setShowDisableARSDialog(false);
  };

  const handleUseDefaultValueChange = (checked: boolean) => {
    if (checked) {
      onFormChange({
        ...editData,
        xrs_maximum_categories: DEFAULT_MAX_CATEGORIES,
        xrs_maximum_products: DEFAULT_MAX_PRODUCTS,
        xrs_use_default_value: true,
      });
      // Clear any errors
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.xrs_maximum_categories;
        delete newErrors.xrs_maximum_products;
        return newErrors;
      });
    }
  };

  const handleMaxCategoriesChange = (value: string) => {
    const numValue = parseInt(value, 10);

    // Validate
    const error = validateMaxCategories(numValue);
    if (error) {
      setErrors((prev) => ({ ...prev, xrs_maximum_categories: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.xrs_maximum_categories;
        return newErrors;
      });
    }

    onFormChange({
      ...editData,
      xrs_maximum_categories: numValue,
    });
  };

  const handleMaxProductsChange = (value: string) => {
    const numValue = parseInt(value, 10);

    // Validate
    const error = validateMaxProducts(numValue);
    if (error) {
      setErrors((prev) => ({ ...prev, xrs_maximum_products: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.xrs_maximum_products;
        return newErrors;
      });
    }

    onFormChange({
      ...editData,
      xrs_maximum_products: numValue,
    });
  };

  const handleMaxCategoriesBlur = () => {
    const error = validateMaxCategories(editData.xrs_maximum_categories);
    if (error) {
      setErrors((prev) => ({ ...prev, xrs_maximum_categories: error }));
    }
  };

  const handleMaxProductsBlur = () => {
    const error = validateMaxProducts(editData.xrs_maximum_products);
    if (error) {
      setErrors((prev) => ({ ...prev, xrs_maximum_products: error }));
    }
  };

  const handleCopyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${fieldName} copied to clipboard!`);
    }).catch(() => {
      toast.error(`Failed to copy ${fieldName}`);
    });
  };

  // Check if both recommendation systems are inactive
  const noRecommendationActive = !editData.enable_xrs && !editData.enable_ars;

  // Check if ARS is enabled but all applets are disabled
  const arsActiveButNoApplets =
    editData.enable_ars &&
    editData.ars_applet_1 === 'No' &&
    editData.ars_applet_2 === 'No' &&
    editData.ars_applet_3 === 'No';

  const isXrsDefault =
    editData.xrs_maximum_categories === DEFAULT_MAX_CATEGORIES &&
    editData.xrs_maximum_products === DEFAULT_MAX_PRODUCTS;

  return (
    <div className="tw-space-y-6">
      {/* Section 1: Script Configuration */}
      <FieldTitle label="Script Configuration" />

      {/* Script URL TextField */}
      <TextField
        label="XRS + ARS JS Script URL"
        placeholder="Enter URL"
        value={editData.xrs_ars_js_script_url}
        onChange={(value) => setValue('xrs_ars_js_script_url', value)}
        showCopyButton={true}
        onCopy={() => handleCopyToClipboard(editData.xrs_ars_js_script_url || '', 'Script URL')}
        infoMessage="JavaScript file that loads XRS and ARS recommendation features on your e-commerce site."
        disabled
      />

      {/* Section 2: Enable XRS Toggle */}
      <div className="tw-flex tw-items-center tw-justify-between">
        <FieldTitle label="Enable XRS" />
        <Toggle
          label=""
          checked={editData.enable_xrs}
          onChange={(checked) => setValue('enable_xrs', checked)}
          disabled={!isCompatioAppsEnabled}
        />
      </div>

      {/* Divider */}
      <Divider />

      {/* Section 3: Display Settings - Only visible when Enable XRS is ON */}
      {editData.enable_xrs && (
        <>
          <FieldTitle
            label="Display Settings"
            required={true}
            infoMessage="Configure how many categories and products display in XRS recommendation widgets."
          />

          {/* Two-Column Layout for Display Settings */}
          <div className="tw-flex tw-gap-[8px] tw-items-start tw-w-full">
            <div className="tw-flex-1">
              <TextField
                label="Maximum Categories"
                type="number"
                value={editData.xrs_maximum_categories.toString()}
                onChange={handleMaxCategoriesChange}
                onBlur={handleMaxCategoriesBlur}
                required={true}
                disabled={!isCompatioAppsEnabled}
                error={!!errors.xrs_maximum_categories}
                helperText={errors.xrs_maximum_categories || ''}
                minValue={1}
                maxValue={10}
              />
            </div>
            <div className="tw-flex-1">
              <TextField
                label="Maximum Products"
                type="number"
                value={editData.xrs_maximum_products.toString()}
                onChange={handleMaxProductsChange}
                onBlur={handleMaxProductsBlur}
                required={true}
                disabled={!isCompatioAppsEnabled}
                error={!!errors.xrs_maximum_products}
                helperText={errors.xrs_maximum_products || ''}
                minValue={10}
                maxValue={100}
              />
            </div>
          </div>

          {/* Use Default Value Checkbox */}
          <Checkbox
            label="Use default value"
            checked={isXrsDefault}
            onChange={handleUseDefaultValueChange}
            disabled={!isCompatioAppsEnabled}
          />

          {/* Divider */}
          <Divider />
        </>
      )}

      {/* Section 5: Enable ARS Toggle */}
      <div className="tw-flex tw-items-center tw-justify-between">
        <FieldTitle label="Enable ARS" />
        <Toggle
          label=""
          checked={editData.enable_ars}
          onChange={handleEnableARSChange}
          disabled={!isCompatioAppsEnabled}
        />
      </div>

      {/* Divider */}
      <Divider />

      {/* Warning: No recommendation system active */}
      {noRecommendationActive && isCompatioAppsEnabled && (
        <>
          <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-rounded">
            <svg
              className="tw-w-5 tw-h-5 tw-text-yellow-600 tw-flex-shrink-0 tw-mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="tw-text-sm tw-text-yellow-800">
              No recommendation system is active. Please enable XRS or ARS to show recommendations on your e-commerce site.
            </span>
          </div>
          <Divider />
        </>
      )}

      {/* Section 6: Applets Configuration - Only visible when Enable ARS is ON */}
      {editData.enable_ars && (
        <>
          <FieldTitle label="Applets Configuration" />

          {/* Applet 1 Dropdown */}
          <Dropdown
            label="Applet 1"
            placeholder="Select option"
            value={editData.ars_applet_1}
            onChange={(value) => setValue('ars_applet_1', value)}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ] as DropdownOption[]}
            disabled={!isCompatioAppsEnabled}
          />

          {/* Applet 2 Dropdown */}
          <Dropdown
            label="Applet 2"
            placeholder="Select option"
            value={editData.ars_applet_2}
            onChange={(value) => setValue('ars_applet_2', value)}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ] as DropdownOption[]}
            disabled={!isCompatioAppsEnabled}
          />

          {/* Applet 3 Dropdown */}
          <Dropdown
            label="Applet 3"
            placeholder="Select option"
            value={editData.ars_applet_3}
            onChange={(value) => setValue('ars_applet_3', value)}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ] as DropdownOption[]}
            disabled={!isCompatioAppsEnabled}
          />

          {/* Warning: ARS enabled but no applets active */}
          {isCompatioAppsEnabled && arsActiveButNoApplets && (
            <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-rounded tw-mt-4">
              <svg
                className="tw-w-5 tw-h-5 tw-text-yellow-600 tw-flex-shrink-0 tw-mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="tw-text-sm tw-text-yellow-800">
                ARS is enabled but no applet is active. Enable at least one applet.
              </span>
            </div>
          )}
        </>
      )}

      <ConfirmationDialog
        isOpen={showDisableARSDialog}
        title="Disable ARS?"
        message="Disabling ARS will hide all automated recommendation features on your e-commerce site. Continue?"
        onConfirm={handleDisableARSConfirm}
        onCancel={handleDisableARSCancel}
      />
    </div>
  );
};

export default EnvironmentCommendTab;